import { ISanityVendorConfigs } from '@rbi-ctg/menu';
import {
  IInRestaurantIncentiveInput,
  IInRestaurantOrderLineItem,
  IVendorConfigsInput,
} from 'generated/graphql-gateway';
import { isCombo, isItem } from 'utils/menu/is-menu-type';
import { blockContentToPlainText } from 'utils/sanity';

import { ICartEntryType } from './hooks/use-in-restaurant-redemption-cart';
import type {
  ICart,
  ICartEntry,
  ICartEntryLegacyOffer,
  ICartEntryOffer,
  ICartEntryReward,
} from './hooks/use-in-restaurant-redemption-cart';

// the accepted rbi gateway vendors
// @todo Should we add SimplyDelivery here?
const lineItemVendors = [
  'carrols',
  'carrolsDelivery',
  'hdx',
  'ncr',
  'ncrDelivery',
  'productNumber',
  'productNumberDelivery',
  'qdi',
  'qdiDelivery',
  'qst',
  'rpos',
  'rposDelivery',
  'sicom',
  'sicomDelivery',
  'tablet',
  'tabletDelivery',
];

// filter invalid vendors
export const filterVendorConfigs = (vendorConfig: ISanityVendorConfigs) =>
  Object.keys(vendorConfig).reduce((accum, vendorKey) => {
    if (lineItemVendors.indexOf(vendorKey) !== -1) {
      accum[vendorKey] = vendorConfig[vendorKey];
    }

    return accum;
  }, {});

const getIncentiveVendorConfigs = (
  cartEntry: ICartEntry
): { incentiveVendorConfigs: Array<IVendorConfigsInput> } | null => {
  let incentiveVendorConfigs: Array<IVendorConfigsInput> | undefined;

  if (cartEntry.type === ICartEntryType.LEGACY_OFFER) {
    incentiveVendorConfigs = getOptionVendorConfigs(cartEntry);
  } else {
    const incentives =
      cartEntry.type === ICartEntryType.OFFER
        ? cartEntry.details.offer.incentives
        : cartEntry.details.reward.incentives;

    incentiveVendorConfigs = incentives?.reduce<Array<IVendorConfigsInput>>((acc, incentive) => {
      if (incentive) {
        if ((isItem(incentive) || isCombo(incentive)) && incentive?.vendorConfigs) {
          acc.push(filterVendorConfigs(incentive.vendorConfigs));
        }
      }

      return acc;
    }, []);
  }

  return incentiveVendorConfigs?.length ? { incentiveVendorConfigs } : null;
};

const getOptionVendorConfigs = (
  cartEntryLegacyOffer: ICartEntryLegacyOffer
): IVendorConfigsInput[] => {
  const { offer } = cartEntryLegacyOffer.details;
  const vendorConfigs: IVendorConfigsInput[] = [];

  if ((isItem(offer.option) || isCombo(offer.option)) && offer.option?.vendorConfigs) {
    vendorConfigs.push(filterVendorConfigs(offer.option.vendorConfigs));
  }

  return vendorConfigs;
};

const getNameFromCartEntry = (offerCartEntry: ICartEntryOffer | ICartEntryLegacyOffer): string => {
  const { offer } = offerCartEntry.details;
  const stringOfferName = blockContentToPlainText(offer?.name?.localeRaw || []);

  return stringOfferName;
};

const getLocalizedPropFromCartEntry = (
  cartEntry: ICartEntryOffer | ICartEntryLegacyOffer,
  propName: 'name' | 'description'
): Pick<IInRestaurantIncentiveInput, 'name' | 'description'> | null => {
  if (!propName) {
    return null;
  }
  const offer = cartEntry.details.offer;
  const stringOffer = blockContentToPlainText(offer[propName]?.localeRaw || []);

  return { [propName]: stringOffer };
};

const rewardOrderLineItem = (cartEntry: ICartEntryReward): IInRestaurantOrderLineItem => {
  const { quantity, referenceId, type } = cartEntry;
  const { engineReward, reward } = cartEntry.details;

  return {
    incentive: {
      id: engineReward.id,
      ...getIncentiveVendorConfigs(cartEntry),
      type,
    },
    quantity,
    referenceId,
    vendorConfigs: filterVendorConfigs(reward.vendorConfigs as ISanityVendorConfigs),
  };
};

const offerOrderLineItem = (
  cartEntry: ICartEntryOffer | ICartEntryLegacyOffer
): IInRestaurantOrderLineItem => {
  const { quantity, referenceId, type } = cartEntry;
  const { offer } = cartEntry.details;
  const { shortCode: offerCode } = offer;
  let id: string;

  if (cartEntry.type === ICartEntryType.OFFER) {
    id = cartEntry.details.offer.loyaltyEngineId!;
  } else {
    id = cartEntry.details.offer._id;
  }
  const name = getNameFromCartEntry(cartEntry);

  return {
    incentive: {
      id,
      ...getLocalizedPropFromCartEntry(cartEntry, 'name'),
      ...getLocalizedPropFromCartEntry(cartEntry, 'description'),
      ...getIncentiveVendorConfigs(cartEntry),
      name,
      type,
      ...(offerCode && { offerCode }),
    },
    quantity,
    referenceId,
    vendorConfigs: filterVendorConfigs(offer.vendorConfigs as ISanityVendorConfigs),
  };
};

const generateOrderLineItemsMap: {
  [key in ICartEntryType]: (ce: ICartEntry) => IInRestaurantOrderLineItem;
} = {
  [ICartEntryType.REWARD]: rewardOrderLineItem,
  [ICartEntryType.OFFER]: offerOrderLineItem,
  [ICartEntryType.LEGACY_OFFER]: offerOrderLineItem,
};

export const generateOrderLineItems = (cart: ICart): Array<IInRestaurantOrderLineItem> =>
  cart.map((cartEntry: ICartEntry) => generateOrderLineItemsMap[cartEntry.type](cartEntry));
